import { computed, shallowRef, toValue, watch, type MaybeRefOrGetter } from 'vue'
import { useWebSocket } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { auth } from '@/stores/auth'

export type RealtimeBusNotification<TMessage = unknown> = {
  id?: number
  channel?: string | unknown[]
  message?: TMessage
  type?: string
}

export function normalizeRealtimeBusMessage<TMessage>(input: unknown): TMessage {
  const message = (input ?? {}) as any
  const payload = message.payload ?? {}
  const data = payload.data ?? {}

  return {
    ...message,
    ...payload,
    ...data,
    event: message.event ?? message.type ?? payload.type ?? data.event ?? data.type
  } as TMessage
}

type UseRealtimeBusOptions<TMessage = unknown> = {
  channels?: MaybeRefOrGetter<string[]>
  enabled?: MaybeRefOrGetter<boolean>
  onNotification?: (notification: RealtimeBusNotification<TMessage>) => void
  onConnected?: () => void
}

export function useRealtimeBus<TMessage = unknown>({
  channels = [],
  enabled = true,
  onNotification,
  onConnected
}: UseRealtimeBusOptions<TMessage>) {
  const authStore = auth()
  const { user } = storeToRefs(authStore)
  const lastNotificationId = shallowRef(0)
  const isLoadingSessionInfo = shallowRef(false)

  const resolvedChannels = computed(() => toValue(channels).filter(Boolean))
  const isEnabled = computed(
    () => toValue(enabled) && (resolvedChannels.value.length > 0 || Boolean(onConnected))
  )
  const workerVersion = computed(() => user.value?.websocket_worker_version)
  const wsUrl = computed(() => {
    if (!isEnabled.value || !workerVersion.value || typeof window === 'undefined') {
      return undefined
    }

    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
    return `${protocol}://${window.location.host}/api-proxy/websocket?version=${encodeURIComponent(workerVersion.value)}`
  })

  function subscribe() {
    if (!resolvedChannels.value.length) return

    socket.send(
      JSON.stringify({
        event_name: 'subscribe',
        data: {
          channels: resolvedChannels.value,
          last: lastNotificationId.value
        }
      })
    )
  }

  function handleConnected() {
    subscribe()
    onConnected?.()
  }

  function handleMessage(event: MessageEvent) {
    let notifications: RealtimeBusNotification<TMessage>[]

    try {
      const parsed = JSON.parse(String(event.data))
      notifications = Array.isArray(parsed) ? parsed : [parsed]
    } catch (error) {
      console.error('Failed to parse realtime bus message:', error)
      return
    }

    for (const notification of notifications) {
      if (typeof notification.id === 'number') {
        lastNotificationId.value = notification.id
      }

      onNotification?.(notification)
    }
  }

  const socket = useWebSocket(wsUrl, {
    immediate: true,
    autoReconnect: {
      retries: () => isEnabled.value,
      delay: 1000
    },
    onConnected: handleConnected,
    onMessage: (_ws, event) => handleMessage(event),
    onError: (_ws, event) => {
      console.error('Realtime bus websocket error:', event)
    }
  })

  watch(resolvedChannels, () => {
    if (socket.status.value === 'OPEN') {
      subscribe()
    }
  })

  watch(
    [isEnabled, workerVersion],
    async ([enabled, version]) => {
      if (!enabled || version || isLoadingSessionInfo.value) return

      isLoadingSessionInfo.value = true
      try {
        await authStore.init()
      } finally {
        isLoadingSessionInfo.value = false
      }
    },
    { immediate: true }
  )

  return {
    ...socket,
    lastNotificationId
  }
}
